import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const EVENT_PROPOSAL_TEMPLATES: string[] = [
  "Corporate Event Staffing",
  "Concert & Festival Crew",
  "Wedding & Social Event Staff",
  "Sports Event Personnel",
  "Trade Show & Expo Staff",
];

export const EVENT_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Standard Event Staffing Agreement",
    description: "General event staffing contract covering crew deployment, shift schedules, labor costs, overtime, and on-site logistics.",
    content: `EVENT STAFFING SERVICES AGREEMENT

This Event Staffing Services Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. SCOPE OF SERVICES

Provider agrees to supply qualified event staffing personnel for Client's event(s) as described below:

{{project_description}}

──────────────────────────────────────────────────

2. CREW PLAN & DEPLOYMENT

{{scope_of_work}}

──────────────────────────────────────────────────

3. SCHEDULE & HOURS

Provider shall staff the event for approximately {{estimated_hours}} total labor hours across all assigned crew members and shifts.

All crew members will report to the designated staging area no later than 30 minutes before their scheduled shift ("crew call"). Late arrivals will be replaced at Provider's discretion.

──────────────────────────────────────────────────

4. COMPENSATION & LABOR COSTS

Client agrees to pay Provider the total estimated labor cost of \${{estimated_cost}} for the services described herein.

Payment Terms: {{payment_terms}}

Invoices shall be issued upon completion of the event (or per shift for multi-day events) and are due within the stated payment terms. Late payments are subject to a 1.5% monthly finance charge.

──────────────────────────────────────────────────

5. OVERTIME & ADDITIONAL HOURS

Any hours worked beyond the agreed shift length or outside the scheduled event window shall be billed at the overtime rate specified in the proposal (typically 1.5× the standard hourly rate).

Client must authorize overtime in writing or verbally to the on-site crew lead. Unauthorized overtime resulting from Client-directed work will still be billed.

──────────────────────────────────────────────────

6. CREW CONDUCT & DRESS CODE

All crew members will:
• Arrive in the agreed-upon uniform or dress code
• Follow Client's venue rules and safety protocols
• Maintain professional conduct at all times
• Report to the designated crew lead or Client point-of-contact

Provider reserves the right to remove and replace any crew member who fails to meet professional standards.

──────────────────────────────────────────────────

7. CLIENT RESPONSIBILITIES

Client agrees to:
• Provide a safe working environment for all crew members
• Supply necessary equipment, tools, or materials as agreed
• Designate a point-of-contact available during all event hours
• Provide crew break areas and access to water/restrooms
• Communicate any changes to the event schedule at least 48 hours in advance

──────────────────────────────────────────────────

8. CANCELLATION & RESCHEDULING

• Cancellation with 7+ days notice: No charge
• Cancellation with 3–6 days notice: 25% of total estimated cost
• Cancellation with less than 72 hours notice: 50% of total estimated cost
• No-show or same-day cancellation: 100% of total estimated cost

Rescheduling requests are subject to crew availability and must be confirmed in writing.

──────────────────────────────────────────────────

9. LIABILITY & INDEMNIFICATION

Provider maintains general liability insurance covering crew members during the event. Provider is not liable for damages caused by Client's guests, attendees, or third parties.

Client agrees to indemnify and hold Provider harmless from claims arising from:
• Unsafe venue conditions
• Client-directed activities outside the agreed scope
• Actions of Client's employees, guests, or vendors

──────────────────────────────────────────────────

10. CONFIDENTIALITY

Both parties agree to keep confidential all proprietary information, pricing, and business details shared under this Agreement.

──────────────────────────────────────────────────

11. ADDITIONAL NOTES

{{proposal_notes}}

──────────────────────────────────────────────────

12. SIGNATURES

By signing below, both parties agree to the terms and conditions outlined in this Agreement.

PROVIDER:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________

CLIENT:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________`,
  },
  {
    name: "Festival & Concert Crew Contract",
    description: "Multi-day event staffing contract for festivals, concerts, and large-scale entertainment events with load-in/load-out provisions.",
    content: `FESTIVAL & CONCERT CREW STAFFING CONTRACT

This Festival & Concert Crew Staffing Contract ("Contract") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. EVENT DESCRIPTION

{{project_description}}

──────────────────────────────────────────────────

2. CREW DEPLOYMENT & ROLES

Provider shall supply the following crew for the event:

{{scope_of_work}}

Total estimated labor hours: {{estimated_hours}}

──────────────────────────────────────────────────

3. LOAD-IN / LOAD-OUT

Provider's crew will be available for load-in and load-out as scheduled. Load-in and load-out hours are billed at the standard hourly rate unless otherwise agreed.

Client shall provide safe access to the venue during load-in/load-out, including loading docks, freight elevators, and staging areas.

──────────────────────────────────────────────────

4. COMPENSATION

Total estimated event cost: \${{estimated_cost}}

Payment Terms: {{payment_terms}}

A 50% deposit is required no later than 7 days before the event start date. The remaining balance is due within 14 days of event completion.

──────────────────────────────────────────────────

5. OVERTIME & WEATHER DELAYS

Overtime applies to hours exceeding the agreed shift length at 1.5× the standard rate. Weather delays that extend crew hours will be billed at the overtime rate.

In the event of a weather cancellation, Client will be billed for all hours already worked plus a 25% cancellation fee on remaining scheduled hours.

──────────────────────────────────────────────────

6. SAFETY & COMPLIANCE

Provider's crew will comply with all applicable OSHA regulations and venue safety requirements. Client shall provide:
• Safety briefings for venue-specific hazards
• Personal protective equipment (PPE) for specialized tasks
• Emergency evacuation procedures

──────────────────────────────────────────────────

7. CREW WELFARE

Client shall provide:
• Designated break areas with shade/shelter
• Potable water and restroom access
• Meal provisions for shifts exceeding 6 hours

──────────────────────────────────────────────────

8. LIABILITY & INSURANCE

Provider carries general liability and workers' compensation insurance. Provider is not liable for damage to Client's equipment, venue property, or injuries to attendees unless caused by Provider's gross negligence.

──────────────────────────────────────────────────

9. CANCELLATION

• 14+ days notice: Full refund of deposit
• 7–13 days notice: 50% of deposit retained
• Less than 7 days: Full deposit retained
• Day-of cancellation: Full contract amount due

──────────────────────────────────────────────────

10. ADDITIONAL TERMS

{{proposal_notes}}

{{terms_and_conditions}}

──────────────────────────────────────────────────

11. SIGNATURES

By signing below, both parties agree to the terms outlined in this Contract.

PROVIDER:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________

CLIENT:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________`,
  },
  {
    name: "Corporate Event Staffing Agreement",
    description: "Professional staffing contract for corporate events, conferences, and trade shows with confidentiality and brand compliance clauses.",
    content: `CORPORATE EVENT STAFFING AGREEMENT

This Corporate Event Staffing Agreement ("Agreement") is entered into as of {{date}} by and between:

PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
(hereinafter referred to as "Provider")

CLIENT:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Client")

──────────────────────────────────────────────────

1. EVENT OVERVIEW

{{project_description}}

──────────────────────────────────────────────────

2. STAFFING PLAN

{{scope_of_work}}

Total estimated labor hours: {{estimated_hours}}

──────────────────────────────────────────────────

3. PROFESSIONAL STANDARDS

All crew members assigned to this event will:
• Maintain a professional appearance consistent with Client's brand standards
• Complete any Client-required training or orientation prior to the event
• Sign non-disclosure agreements if required by Client
• Represent Client's brand in a positive and professional manner

──────────────────────────────────────────────────

4. COMPENSATION

Total estimated cost: \${{estimated_cost}}

Payment Terms: {{payment_terms}}

All pricing is based on the crew plan and shift schedule outlined in the associated staffing proposal. Any changes to crew size, shift length, or event duration may result in adjusted costs.

──────────────────────────────────────────────────

5. CONFIDENTIALITY

Provider and all assigned crew members agree to maintain strict confidentiality regarding:
• Client's proprietary business information
• Event details, guest lists, and VIP information
• Product launches, announcements, or unpublished materials
• Any information marked as confidential by Client

This obligation survives termination of this Agreement.

──────────────────────────────────────────────────

6. CLIENT RESPONSIBILITIES

Client agrees to:
• Provide event schedule, floor plans, and briefing materials at least 72 hours before the event
• Designate an on-site liaison for crew coordination
• Provide meals for crew members on shifts exceeding 5 hours
• Ensure a safe working environment

──────────────────────────────────────────────────

7. CANCELLATION & MODIFICATION

• 10+ business days notice: No charge
• 5–9 business days notice: 25% of total cost
• Less than 5 business days: 50% of total cost
• Less than 48 hours: 75% of total cost

Scope modifications during the event will be documented and billed accordingly.

──────────────────────────────────────────────────

8. LIABILITY

Provider maintains appropriate insurance coverage. Provider's total liability under this Agreement shall not exceed the total contract value.

──────────────────────────────────────────────────

9. ADDITIONAL NOTES

{{proposal_notes}}

──────────────────────────────────────────────────

10. SIGNATURES

By signing below, both parties agree to the terms of this Agreement.

PROVIDER:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________

CLIENT:
Name: ____________________________
Title: ____________________________
Signature: ________________________
Date: ____________________________`,
  },
];

export const EVENT_PRICING_MODELS: Record<string, unknown> = {};

export const EVENT_TERMINOLOGY = {
  proposalLabel: "Staffing Proposal",
  contractLabel: "Staffing Agreement",
  staffLabel: "Event Crew",
  rateLabel: "Hourly Rate",
};
